import React, { useEffect } from "react";
import { HashRouter as Router, Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Dashboard   from "./pages/Dashboard";
import Signals     from "./pages/Signals";
import TradeHistory from "./pages/TradeHistory";
import Accounts    from "./pages/Accounts";
import Profile     from "./pages/Profile";
import Users       from "./pages/Users";
import Downloads   from "./pages/Downloads";
import Documentation from "./pages/Documentation";
import Login       from "./pages/Login";
import Signup      from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PanelLogs   from "./pages/PanelLogs";
import LandingPage from "./pages/LandingPage";
import RenewPlan   from "./pages/RenewPlan";
import AdminPlans  from "./pages/AdminPlans";
import Support     from "./pages/Support";
import Notifications from "./pages/Notifications";
import { logout }  from "./api/client";
import {
  LayoutDashboard,
  Users as UsersIcon,
  Radio,
  BarChart2,
  Monitor,
  Download,
  BookOpen,
  User as UserIcon,
  LogOut,
  Zap,
  History,
  CreditCard,
  Sparkles,
  Bell
} from "lucide-react";
import BackgroundAnimation from "./components/BackgroundAnimation";
import NotificationsBell from "./components/NotificationsBell";
import ChatBot from "./components/ChatBot";
import AnnouncementBar from "./components/AnnouncementBar";
import "./App.css";

function Layout() {
  const { user, clearAuth } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try { await logout(); } catch {}
    clearAuth();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: <LayoutDashboard size={18} />, label: "Dashboard" },
    ...(user?.role === "admin" ? [
      { to: "/users", icon: <UsersIcon size={18} />, label: "Users" },
      { to: "/admin-plans", icon: <Sparkles size={18} />, label: "Manage Plans" }
    ] : []),
    { to: "/signals", icon: <Radio size={18} />, label: "Signals" },
    { to: "/trades", icon: <BarChart2 size={18} />, label: "Trades" },
    { to: "/accounts", icon: <Monitor size={18} />, label: "Trading Accounts" },
    { to: "/logs", icon: <History size={18} />, label: user?.role === "admin" ? "System Logs" : "Activity Logs" },
    { to: "/downloads", icon: <Download size={18} />, label: "Downloads" },
    { to: "/docs", icon: <BookOpen size={18} />, label: "Documentation" },
    ...(user?.role !== "admin" ? [
      { to: "/renew-plan", icon: <CreditCard size={18} />, label: "Pricing & Plans" }
    ] : []),
    { to: "/notifications", icon: <Bell size={18} />, label: "Notifications" },
    { to: "/profile", icon: <UserIcon size={18} />, label: "Settings" }
  ];

  return (
    <div className="app-container">
      <AnnouncementBar />
      <div className="app">
        <BackgroundAnimation />
        <aside className="sidebar">
        <div className="brand" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", paddingLeft: "28px", paddingRight: "28px", paddingBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Zap className="brand-icon" size={24} fill="currentColor" />
            <span className="brand-text">PINE <span className="accent">CONNECTOR</span></span>
          </div>
          <NotificationsBell />
        </div>
        <nav>
          {navItems.map(({ to, icon, label }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}
              end={to === "/dashboard"}>
              <span className="nav-icon">{icon}</span>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User info at bottom */}
        <div className="sidebar-user" style={{ justifyContent: "center" }}>
          <div className="user-avatar">{(user?.name?.charAt(0) || "U").toUpperCase()}</div>
          <div className="user-info" style={{ marginRight: 0 }}>
            <div className="user-name">{user?.name || "User"}</div>
            {user?.role === "admin" ? (
              <div className="user-role">Administrator</div>
            ) : (
              <Link to="/renew-plan" className="user-role" style={{ textDecoration: "none", cursor: "pointer", color: "var(--accent)", fontWeight: 700, fontSize: "12px" }} title="Click to manage your plan">
                {user?.service_plan 
                  ? `📦 ${user.service_plan.charAt(0).toUpperCase() + user.service_plan.slice(1).toLowerCase()}` 
                  : "📦 Demo Plan"}
              </Link>
            )}
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="status-dot" style={{
            width: "8px", height: "8px",
            background: "var(--green)",
            borderRadius: "50%",
            boxShadow: "0 0 10px var(--green)",
            animation: "pulse 2s infinite"
          }} />
          <span>Bridge Online</span>
        </div>
      </aside>
      <main className="content">
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/users"    element={<Users />} />
          <Route path="/admin-plans" element={<AdminPlans />} />
          <Route path="/signals"  element={<Signals />} />
          <Route path="/trades"   element={<TradeHistory />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/logs"     element={<PanelLogs />} />
          <Route path="/downloads" element={<Downloads />} />
          <Route path="/docs"     element={<Documentation />} />
          <Route path="/renew-plan" element={<RenewPlan />} />
          <Route path="/profile"  element={<Profile />} />
          <Route path="/support"  element={<Support />} />
          <Route path="/notifications" element={<Notifications />} />
        </Routes>
        <ChatBot />
      </main>
      </div>
    </div>
  );
}

export default function App() {
  // Setup theme initially
  useEffect(() => {
    let savedTheme = localStorage.getItem("pine_connector_theme") || "dark";
    // Migration: If legacy theme is found or unknown, reset it to dark
    const legacyThemes = ["cyberpunk", "emerald", "sunset", "amethyst", "frost", "rose"];
    if (legacyThemes.includes(savedTheme) || (savedTheme !== "dark" && savedTheme !== "light")) {
      savedTheme = "dark";
      localStorage.setItem("pine_connector_theme", "dark");
    }
    THEMES_HELPER.apply(savedTheme);
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/"       element={<LandingPage />} />
          <Route path="/login"  element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

// Simple helper to load theme class
const THEMES_HELPER = {
  apply: (themeId) => {
    const ids = ["cyberpunk", "emerald", "sunset", "amethyst", "frost", "rose", "dark", "light"];
    ids.forEach(id => document.body.classList.remove(`theme-${id}`));
    document.body.classList.add(`theme-${themeId}`);
  }
};
